import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import DateInput from '@/components/ui/date-input';
import { API_BASE } from './wizard/constants';
import { FullscreenShell } from './wizard/shared';

const EMPTY = {
  first_name: '',
  last_name: '',
  street: '',
  house_number: '',
  postal_code: '',
  city: '',
  email: '',
  kassenzeichen: '',
  subject: '',
  notes: '',
  timing: 'next',
  requested_date: '',
};

export default function PublicContractDeclaration({ kind }) {
  const { tenantSlug } = useParams();
  const cancellation = kind === 'cancellation';
  const [school, setSchool] = useState({ tenant_name: '', notice: '' });
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/public/declarations/${encodeURIComponent(tenantSlug)}`)
      .then((r) => { if (!cancelled) setSchool(r.data || {}); })
      .catch(() => { if (!cancelled) setSchool({ tenant_name: '', notice: '' }); });
    return () => { cancelled = true; };
  }, [tenantSlug]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (cancellation && form.timing === 'date' && !form.requested_date) {
      setError('Kündigungsdatum fehlt');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = {
        kind,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        street: form.street.trim(),
        house_number: form.house_number.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        email: form.email.trim(),
        kassenzeichen: form.kassenzeichen.trim(),
        subject: form.subject.trim(),
        notes: form.notes.trim(),
        timing: cancellation ? form.timing : null,
        requested_date: cancellation && form.timing === 'date' ? form.requested_date : null,
      };
      const r = await axios.post(`${API_BASE}/public/declarations/${encodeURIComponent(tenantSlug)}`, body);
      setReceipt(r.data.receipt);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Senden fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const title = cancellation ? 'Vertrag kündigen' : 'Vertrag widerrufen';

  return (
    <FullscreenShell tenantName={school.tenant_name} title={title} slug={tenantSlug}>
      <form onSubmit={submit} className="w-full max-w-3xl mx-auto bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4" data-testid={`public-declaration-${kind}`}>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {!cancellation && school.notice && (
          <article className="whitespace-pre-line text-sm text-slate-700 leading-relaxed border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="public-withdrawal-notice">
            {school.notice}
          </article>
        )}
        {receipt ? (
          <p className="text-sm text-slate-800" data-testid="public-declaration-receipt">{receipt}</p>
        ) : (
          <>
            <div className="text-sm font-medium text-slate-700">Teilnehmer:in</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Vorname *" value={form.first_name} onChange={set('first_name')} testId="public-declaration-first-name" />
              <Field label="Nachname *" value={form.last_name} onChange={set('last_name')} testId="public-declaration-last-name" />
              <Field label="Straße *" value={form.street} onChange={set('street')} testId="public-declaration-street" />
              <Field label="Hausnr. *" value={form.house_number} onChange={set('house_number')} testId="public-declaration-house-number" />
              <Field label="PLZ *" value={form.postal_code} onChange={set('postal_code')} testId="public-declaration-postal-code" />
              <Field label="Ort *" value={form.city} onChange={set('city')} testId="public-declaration-city" />
            </div>
            <Field label="E-Mail *" value={form.email} onChange={set('email')} testId="public-declaration-email" type="email" />
            <Field label="Kassenzeichen (wenn bekannt)" value={form.kassenzeichen} onChange={set('kassenzeichen')} testId="public-declaration-kassenzeichen" />
            <Field label="Unterricht / Fach / Kurs *" value={form.subject} onChange={set('subject')} testId="public-declaration-subject" />
            {cancellation && (
              <fieldset className="space-y-2" data-testid="public-declaration-timing">
                <legend className="text-sm text-slate-700">Art der Erklärung</legend>
                <Radio name="timing" value="next" checked={form.timing === 'next'} onChange={set('timing')} label="Vertrag zum nächstmöglichen Zeitpunkt kündigen" />
                <Radio name="timing" value="date" checked={form.timing === 'date'} onChange={set('timing')} label="Vertrag zu einem anderen Zeitpunkt kündigen" />
                {form.timing === 'date' && (
                  <div>
                    <Label className="mb-1 block">Kündigungsdatum *</Label>
                    <DateInput value={form.requested_date} onChange={set('requested_date')} data-testid="public-declaration-date" />
                  </div>
                )}
              </fieldset>
            )}
            <div>
              <Label className="mb-1 block">Bemerkungen</Label>
              <Textarea value={form.notes} onChange={set('notes')} placeholder="Raum für zusätzliche Bemerkungen" data-testid="public-declaration-notes" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="public-declaration-error">{error}</p>}
            <div className="flex items-center justify-between gap-3">
              <Button type="submit" disabled={submitting} data-testid="public-declaration-submit">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {title}
              </Button>
              <span className="text-xs text-slate-400">* Pflichtangabe</span>
            </div>
          </>
        )}
      </form>
    </FullscreenShell>
  );
}

function Field({ label, value, onChange, testId, type = 'text' }) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      <Input type={type} value={value} onChange={onChange} required={label.includes('*')} data-testid={testId} />
    </div>
  );
}

function Radio({ name, value, checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-800">
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
